import React, { useState } from 'react'
import { Button, DialogActions, DialogContent, DialogTitle, InputLabel, Select, MenuItem, Tooltip, SelectChangeEvent } from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DialogProps } from "../../Dialogs/DialogProps";
import { IAppCustomer } from '../../../Interfaces/Application/IAppCustomer';
import { hasValue } from "@amsanalytics/react-shared-library";

export interface UnmergeAppDialogResult {
  appCustomerIdToArchive: string;
  primaryAppCustomerId: string;
}

interface UnmergeAppDialogProps extends DialogProps<null> {
  appCustomers: IAppCustomer[];
  applicationNumber: number;
  onConfirm(result: UnmergeAppDialogResult): void;
}

const UnmergeAppDialog: React.FC<UnmergeAppDialogProps> = (props) => {
  const [dialogResult, setDialogResult] = useState<UnmergeAppDialogResult>(
    { appCustomerIdToArchive: '', primaryAppCustomerId: '' }
  );
  const applicantToRemove = props.appCustomers.find(ac => ac.id.toUpperCase() === dialogResult.appCustomerIdToArchive.toUpperCase());
  const applicantToRemoveName = applicantToRemove
    ? `${applicantToRemove.customer.firstName ?? ''} ${applicantToRemove.customer.lastName ?? ''}`.trim()
    : '';
  return (
    <>
      <DialogTitle id="form-dialog-title">Unmerge Application</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: '16px', marginTop: 0, marginBottom: '1em', maxWidth: '500px' }}>
          You are unmerging application <b>{props.applicationNumber}</b>. Select the applicant that you want to remove.
          The removed applicant will be moved to their own application, and the remaining applicant{props.appCustomers.length > 2 ? 's' : ''} will stay on a new application.
          {props.appCustomers.length > 2 && ' Then select which of the remaining applicants should be primary.'}
        </p>
        <InputLabel id="select-archive-customer-label">Applicant to remove</InputLabel>
        <Select
          name="applicatoinSelect"
          fullWidth
          labelId="select-archive-customer-label"
          id="select"
          value={dialogResult.appCustomerIdToArchive}
          onChange={(event: SelectChangeEvent<string>, child: React.ReactNode) => {
            setDialogResult({
              ...dialogResult,
              appCustomerIdToArchive: event.target.value as string,
              primaryAppCustomerId: props.appCustomers.find(ac => ac.id.toUpperCase() !== (event.target.value as string).toUpperCase())?.id || ''
            })
          }}
        >
          <MenuItem value="" disabled>
            Select an applicant...
          </MenuItem>
          {props.appCustomers.sort(customer => customer.customerRole?.isPrimaryApplicant ? -1 : 1).map((ac: IAppCustomer, key: number) => (
            <MenuItem key={key} value={ac.id}>{ac.customer.firstName} {ac.customer.lastName} &bull; {ac.customerRole.name}</MenuItem>
          ))}
        </Select>
        {/* If three customers exist for this application, ask the user which of the remaining two customers should be the primary applicant after unmerging. */}
        {
          props.appCustomers.length > 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', margin: '1em 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1px' }}>
                <InputLabel id="select-primary-customer-label" style={{ marginRight: '5px' }}>Primary applicant after removal</InputLabel>
                <Tooltip title="Select which of the remaining applicants should be the primary applicant on the new application." placement='right'>
                  <InfoOutlinedIcon color='primary' />
                </Tooltip>
              </div>
              <Select
                name="primaryApplicantSelect"
                fullWidth
                labelId="select-primary-customer-label"
                id="select"
                disabled={!hasValue(dialogResult.appCustomerIdToArchive)}
                value={dialogResult.primaryAppCustomerId}
                onChange={(event: SelectChangeEvent<string>, child: React.ReactNode) => {
                  setDialogResult({
                    ...dialogResult,
                    primaryAppCustomerId: event.target.value as string
                  })
                }}
              >
                {hasValue(dialogResult.appCustomerIdToArchive) && props.appCustomers.filter(ac => ac.id.toUpperCase() !== dialogResult.appCustomerIdToArchive!.toUpperCase()).sort(customer => customer.customerRole?.isPrimaryApplicant ? -1 : 1).map((ac: IAppCustomer, key: number) => (
                  <MenuItem key={key} value={ac.id}>{ac.customer.firstName} {ac.customer.lastName} &bull; {ac.customerRole.name}</MenuItem>
                ))}
              </Select>
            </div>
          )
        }
      </DialogContent>
      <DialogActions>
        <Button
          onClick={props.closeDialog}
          variant='outlined'
          color="inherit"
          style={{ marginRight: '5px' }}>
          Cancel
        </Button>
        <Button
          onClick={() => props.onConfirm(dialogResult)}
          disabled={!hasValue(dialogResult.appCustomerIdToArchive) || (props.appCustomers.length > 2 && !hasValue(dialogResult.primaryAppCustomerId))}
          color="primary"
          variant='contained'>
          {applicantToRemoveName ? `Remove ${applicantToRemoveName}` : 'Remove'}
        </Button>
      </DialogActions>
    </>
  )
}

export default UnmergeAppDialog;